import { zodResolver } from '@hookform/resolvers/zod';
import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import Checkbox from '@ui/forms/Checkbox';
import PhoneTextInput from '@ui/forms/PhoneTextInput';

import SubmitButton from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { commonErrors } from '@utils/error';
import { checkbox, requiredCheckbox } from '@utils/validation';

import { AuthStep, AuthStepContext, useAuth } from '@providers/AuthProvider';

import awsAuthErrorMessages from '../awsAuthErrorMessages';

interface IPhoneNumberEnrollmentFormProps {
	pageContent: IPhoneNumberEnrollmentFormContent;
	authStep: AuthStepContext;
	setAuthStep: (authContext: AuthStepContext) => void;
	setPhoneNumber: Dispatch<SetStateAction<string>>;
	setNotify?: Dispatch<SetStateAction<boolean | 'true' | null | undefined>>;
	isSignUp: boolean;
}

export interface IPhoneNumberEnrollmentFormContent {
	title: string;
	description: string;
	checkboxLabel: string;
	disclaimer: string;
}

const FullWidthColumn = fullWidth<IFlexColumn>(spaced<IFlexColumn>(FlexColumn));

const PhoneNumberEnrollmentForm: FC<IPhoneNumberEnrollmentFormProps> = ({
	pageContent,
	authStep,
	setAuthStep,
	setPhoneNumber,
	setNotify,
	isSignUp,
}) => {
	const { title, description, checkboxLabel, disclaimer } = pageContent;
	const [submitting, setSubmitting] = useState(false);

	const { setUpSMSMultifactor } = useAuth();
	const schema = z.object({
		phoneNumber: z.string({ required_error: commonErrors.inputRequired }).min(9),
		notify: checkbox,
		mfaConsent: requiredCheckbox(commonErrors.checkRequired),
	});

	type Schema = Partial<z.input<typeof schema>>;

	const { handleSubmit, register, control, formState, setError } = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	const { errors, isSubmitted } = formState;
	const onSubmit = async (data: Schema) => {
		try {
			setSubmitting(true);
			setPhoneNumber(data.phoneNumber as string);
			if (setNotify) setNotify(data.notify);
			const newAuthStep = await setUpSMSMultifactor(authStep.user, data.phoneNumber ?? '');

			setAuthStep(newAuthStep);
		} catch (e: any) {
			if (
				e?.message === awsAuthErrorMessages.expiredSession.awsErrorMessage ||
				e?.message === awsAuthErrorMessages.invalidSession.awsErrorMessage
			) {
				setAuthStep({ nextStep: AuthStep.needPassword });
			}

			setError('phoneNumber', {
				type: 'manual',
				message: awsAuthErrorMessages.invalidPhoneNumber.userErrorMessage,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FlexColumn gap="md">
				<FlexColumn gap="sm">
					<HeadingText>{title}</HeadingText>
					<BasicText>{description}</BasicText>
				</FlexColumn>
				<FullWidthColumn gap="sm" marginBottom="md">
					<Controller
						name="phoneNumber"
						control={control}
						render={({ field }) => (
							<PhoneTextInput
								placeholder="(xxx) xxx-xxxx"
								label="Phone number"
								error={isSubmitted && !!errors?.phoneNumber}
								errorLabel={errors?.phoneNumber?.message ?? commonErrors.inputRequired}
								type="tel"
								{...field}
							/>
						)}
					/>
					<Checkbox
						label={disclaimer}
						textSize="xxs"
						errorLabel={errors.mfaConsent?.message}
						{...register('mfaConsent')}
					/>
					{isSignUp && (
						<Checkbox
							label={checkboxLabel}
							textSize="xxs"
							errorLabel={errors.notify?.message}
							{...register('notify')}
						/>
					)}
				</FullWidthColumn>
				<FlexColumn gap="sm">
					<SubmitButton
						type="submit"
						label="Send code"
						variant="secondary"
						submitting={submitting}
					/>
				</FlexColumn>
			</FlexColumn>
		</form>
	);
};

export default PhoneNumberEnrollmentForm;
